import type { ReactNode } from 'react';

import { useRouteLoaderData, useSearchParams } from '@remix-run/react';
import type { TSignupElement } from '@shared/dream-components';
import { SignupElement } from '@shared/dream-components';

import SignupAction from '~/components/SignupAction';
import useIsLoggedIn from '~/hooks/useIsLoggedIn';
import type { RootLoaderData } from '~/root';

import useGDPRTranslations from './hooks/useGDPRTranslations';

interface Props {
  node: TSignupElement;
  children: ReactNode | ReactNode[];
  premiumOfferId?: string;
}

const Signup = ({ node, children, premiumOfferId }: Props) => {
  const { site } = useRouteLoaderData('root') as RootLoaderData;
  const [searchParams] = useSearchParams();
  const gdprTranslations = useGDPRTranslations();
  const premiumOfferIdFromUrl = searchParams.get('premium_offer_id') || undefined;
  const isSignupModal = searchParams.get('modal') === 'signup';
  const isPremiumOfferSignup = premiumOfferIdFromUrl && isSignupModal;

  const isLoggedIn = useIsLoggedIn();
  const showWhenLoggedIn = node?.attrs?.showWhenLoggedIn;
  const hideForm = !showWhenLoggedIn && isLoggedIn && !isPremiumOfferSignup;

  if (hideForm) {
    return null;
  }

  return (
    <SignupAction
      id={node.attrs.id}
      signupFlowId={node?.attrs.signupFlowId}
      automationIds={node?.attrs.automationIds}
      premiumOfferId={premiumOfferId || premiumOfferIdFromUrl}
      redirectUrl={node?.attrs.redirectUrl}
      customFormType={node?.attrs.customFormType}
    >
      {({ isLoading, setEmail }) => (
        <SignupElement
          element={node}
          isSubmitting={isLoading}
          isLive
          hasTermsOfService={!!site.viewable_site_version.settings.terms_of_service_enabled}
          hasCustomTermsOfService={site.viewable_site_version.settings.terms_of_service}
          onEmailChange={setEmail}
          gdprTranslations={gdprTranslations}
        >
          {children}
        </SignupElement>
      )}
    </SignupAction>
  );
};

export default Signup;
